import {RequestMethod} from '#lit/common/constants/index.js';
import type {Audience, AudienceTemplate} from '#lit/types/audiences.js';
import type {DatasetGroup} from '#lit/types/datasets.js';
import type {Either} from '#lit/types/Either.js';
import {left, right} from '#lit/types/Either.js';
import {FlyErrorWarning, isAbortError} from '#lit/utils/errors.js';
import type {RequestExecutionHandler} from '#lit/utils/http.js';
import {fetcher, RequestHandler} from '#lit/utils/http.js';
import {displayApiError} from '#lit/utils/notifications.js';
import type {RequestManagerError} from './errors.js';
import {
  AbortedRequestError,
  AudienceNameConflictError,
  DuplicatedRequestError,
  InvalidEndpointError,
} from './errors.js';
import {isDefined} from '#lit/utils/index.js';
import {audienceBuilderLang} from '#locale/translations/audiences/builder.js';
import {locale} from '#locale';

const AUDIENCE_BUILDER_ERROR_TIMEOUT_MS = 45000;
const lang = audienceBuilderLang[locale.current];

export type ABEndpoint = {
  key: ABRequestKeys;
  method: RequestMethod;
  url: string;
};

export enum ABRequestKeys {
  SAVE = 'save',
  UPDATE = 'update',
  INSIGHTS = 'insights',
  ADMIN_QUERY = 'admin-query',
}

const audienceNewEndpoint = (id: number): ABEndpoint => {
  return {
    key: ABRequestKeys.SAVE,
    method: RequestMethod.POST,
    url: `/api/v2/dataset_groups/${id}/audiences`,
  };
};

const updateEndpoint = (dgid: number, id: number): ABEndpoint => {
  return {
    key: ABRequestKeys.UPDATE,
    method: RequestMethod.PATCH,
    url: `/api/v2/dataset_groups/${dgid}/audiences/${id}`,
  };
};

const insightsEndpoint = (audienceId: number | undefined): ABEndpoint => {
  const audienceParam = isDefined(audienceId)
    ? `?audience_id=${audienceId}`
    : '';
  return {
    key: ABRequestKeys.INSIGHTS,
    method: RequestMethod.POST,
    url: `/api/v2/quervice/report${audienceParam}`,
  };
};

const queryEndpoint = (audienceId: number | undefined): ABEndpoint => {
  const audienceParam = isDefined(audienceId)
    ? `?audience_id=${audienceId}`
    : '';
  return {
    key: ABRequestKeys.ADMIN_QUERY,
    method: RequestMethod.POST,
    url: `/api/v2/quervice/query${audienceParam}`,
  };
};

type NameConflictError = {
  errors: {name?: string[]}[];
};

const isNameConflictError = (err: unknown): err is NameConflictError => {
  return (err as NameConflictError).errors?.some(e =>
    e.name?.includes('taken')
  );
};

export class ABRequestManager {
  endpoints = new Map<ABRequestKeys, ABEndpoint>();
  signals = new Map<ABRequestKeys, AbortSignal>();
  handler!: RequestHandler;
  private requestsMap = new Map<ABRequestKeys, RequestExecutionHandler>();
  private currentRequests = new Set<ABRequestKeys>();
  private duplicatedRequests = new Set<ABRequestKeys>();

  constructor(
    public audience?: Audience | AudienceTemplate,
    public datasetGroup?: DatasetGroup
  ) {
    this.audience = audience;
    this.datasetGroup = datasetGroup;
    this.endpoints.set(ABRequestKeys.INSIGHTS, insightsEndpoint(audience?.id));
    this.endpoints.set(ABRequestKeys.ADMIN_QUERY, queryEndpoint(audience?.id));
    if (datasetGroup) {
      this.endpoints.set(
        ABRequestKeys.SAVE,
        audienceNewEndpoint(datasetGroup.id)
      );
    }

    if (audience && datasetGroup) {
      this.endpoints.set(
        ABRequestKeys.UPDATE,
        updateEndpoint(datasetGroup.id, audience.id)
      );
    }

    for (const [key, endpoint] of this.endpoints.entries()) {
      this.requestsMap.set(key, this.makeRequest(endpoint));
    }

    this.handler = RequestHandler.create(
      Object.fromEntries(this.requestsMap.entries())
    );
  }

  setAudience(audience: Audience | AudienceTemplate) {
    this.audience = audience;
    if (this.datasetGroup && !this.endpoints.has(ABRequestKeys.UPDATE)) {
      const endpoint = updateEndpoint(this.datasetGroup.id, audience.id);
      const handler = this.makeRequest(endpoint);
      this.endpoints.set(ABRequestKeys.UPDATE, endpoint);
      this.requestsMap.set(ABRequestKeys.UPDATE, handler);
      this.handler.addExecutionHandler(ABRequestKeys.UPDATE, handler);
    }
  }

  setDatasetGroup(datasetGroup: DatasetGroup) {
    this.datasetGroup = datasetGroup;

    if (this.audience && !this.endpoints.has(ABRequestKeys.UPDATE)) {
      const endpoint = updateEndpoint(datasetGroup.id, this.audience.id);
      const handler = this.makeRequest(endpoint);
      this.endpoints.set(ABRequestKeys.UPDATE, endpoint);
      this.requestsMap.set(ABRequestKeys.UPDATE, handler);
      this.handler.addExecutionHandler(ABRequestKeys.UPDATE, handler);
    }

    if (!this.endpoints.has(ABRequestKeys.SAVE)) {
      const endpoint = audienceNewEndpoint(datasetGroup.id);
      const handler = this.makeRequest(endpoint);
      this.endpoints.set(ABRequestKeys.SAVE, endpoint);
      this.requestsMap.set(ABRequestKeys.SAVE, handler);
      this.handler.addExecutionHandler(ABRequestKeys.SAVE, handler);
    }
  }

  makeRequest<Response = unknown>(
    endpoint: ABEndpoint
  ): RequestExecutionHandler {
    const fn = async (
      signal: AbortSignal,
      body?: Record<string, unknown>
    ): Promise<Response | void> => {
      this.signals.set(endpoint.key, signal);

      try {
        const response = await fetcher<Response>(endpoint.url, {
          signal,
          method: endpoint.method,
          body,
        });

        this.signals.delete(endpoint.key);
        return response;
      } catch (err) {
        this.signals.delete(endpoint.key);
        throw err;
      }
    };

    return fn;
  }

  abortRequest(key: ABRequestKeys): void {
    this.handler.abortRequest(key);
  }

  abortAllRequests() {
    for (const key of Object.values(ABRequestKeys)) {
      this.handler.abortRequest(key);
    }
  }

  async execute<Response = unknown>(
    key: ABRequestKeys,
    body: Record<string, unknown> = {},
    opts: {disallowAbort: boolean} = {disallowAbort: false}
  ): Promise<Either<RequestManagerError, Response>> {
    if (this.currentRequests.has(key)) {
      if (opts?.disallowAbort) {
        const err = new DuplicatedRequestError(
          `Skipping request for builder ${key}`
        );
        console.warn(err.message);
        return Promise.resolve(left(err));
      }

      this.duplicatedRequests.add(key);
    }

    this.currentRequests.add(key);

    try {
      if (!this.endpoints.has(key)) {
        return Promise.resolve(
          left(
            new InvalidEndpointError(
              `Attempted to call an invalid endpoint: ${key || 'undefined'}`
            )
          )
        );
      }

      const response = await this.handler.executeRequest(key, body);
      return right(response as Response);
    } catch (err) {
      console.error(err);

      if (this.duplicatedRequests.has(key)) {
        this.duplicatedRequests.delete(key);
        const err = new DuplicatedRequestError(
          `Canceling previous request for builder ${key}`
        );
        console.warn(err.message);
        return left(err);
      }

      if (isAbortError(err)) {
        const err = new AbortedRequestError(
          `Request "${key}" was aborted.`
        ).register('Concern in request manager');
        return left(err);
      }

      if (isNameConflictError(err)) {
        const err = new AudienceNameConflictError().register(
          lang.name.error.taken.title
        );
        return left(err);
      }

      const defaultError = new FlyErrorWarning(
        `Error with ABV2 request for: ${key}`,
        {
          label: 'endpoint',
          data: this.endpoints.get(key)?.url || 'invalid endpoint',
          inline: true,
        },
        {
          label: 'request',
          data: body,
        },
        {
          label: 'response',
          data: err,
        }
      ).register('ABV2 Request Manager Error');

      displayApiError(err, undefined, AUDIENCE_BUILDER_ERROR_TIMEOUT_MS);

      return left(defaultError);
    } finally {
      this.currentRequests.delete(key);
    }
  }

  executeAllRequests(body: Record<string, unknown> = {}) {
    this.handler.executeAllRequests(body);
  }
}
